import { Box, Heading, StackProps, useMediaQuery } from '@chakra-ui/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Chat, ChatList, ChatMessageInput } from '@/components/ui/shared/conversation/Chat';
import { ChatError } from '@/components/ui/shared/conversation/ChatError/ChatError';
import { ChatLoader } from '@/components/ui/shared/conversation/ChatLoader/ChatLoader';
import { ChatUnauthorized } from '@/components/ui/shared/conversation/ChatUnauthorized/ChatUnauthorized';
import { MessageLoader } from '@/components/ui/shared/conversation/MessageLoader/MessageLoader';
import { NestedChatMessage } from '@/components/ui/shared/conversation/NestedChatMessage/NestedChatMessage';
import { NewMessagesNotificationButton } from '@/components/ui/shared/conversation/NewMessagesNotificationButton/NewMessagesNotificationButton';
import { ScrollToBottomButton } from '@/components/ui/shared/conversation/ScrollToBottomButton/ScrollToBottomButton';
import { BugsnagErrorBoundary } from '@/components/ui/shared/utilities/BugsnagErrorBoundary/BugsnagErrorBoundary';
import { ResourceType } from '@/enums';
import useSession from '@/hooks/useSession';
import { IConversationMessage } from '@/utils/conversation/types';

interface IQuestionsAndAnswersProps extends StackProps {
  resourceId: string;
  resourceType: ResourceType;
  conversationSid?: string;
  onMessageSend?: (message: IConversationMessage) => void;
}

export const QuestionsAndAnswers: FC<IQuestionsAndAnswersProps> = ({
  resourceId,
  resourceType,
  conversationSid,
  onMessageSend,
  ...restProps
}) => {
  const { user } = useSession();
  const { t } = useTranslation();
  const [isDesktop] = useMediaQuery('(min-width: 1024px)');

  return (
    <BugsnagErrorBoundary FallbackComponent={ChatError}>
      <Chat
        conversationSid={conversationSid}
        resourceId={resourceId}
        unauthorisedSlot={<ChatUnauthorized />}
        loaderSlot={<ChatLoader />}
        resourceType={resourceType}
        {...restProps}
      >
        {isDesktop ? (
          <Box p={6} alignSelf="start" bg="white" w="full" flexGrow={0} boxShadow="base" zIndex={2}>
            <Heading as="h4" fontSize="24px" lineHeight="32px" fontWeight={700}>
              {t('conversation.questionsChatTitle')}
            </Heading>
          </Box>
        ) : null}

        <ChatList
          minH="0"
          flexGrow={1}
          onMessageSend={onMessageSend}
          scrollButtonSlot={(props) => <ScrollToBottomButton left="calc(100% - 60px)" bottom="20px" {...props} />}
          notificationButtonSlot={(props) => <NewMessagesNotificationButton {...props} />}
          messageLoaderSlot={(props) => <MessageLoader {...props} />}
        >
          {(props) => {
            return <NestedChatMessage {...props} />;
          }}
        </ChatList>
        <ChatMessageInput flexGrow={0} messageAttributes={{ nameToDisplay: user?.fullName, userEmail: user?.email }} />
      </Chat>
    </BugsnagErrorBoundary>
  );
};
